/**
 * Screen Features Entity Schemas
 *
 * Canonical Zod schema for the `screenFeatures` portion of the app
 * configuration. Screen features are per-screen UI configuration
 * (home / groups / directory / personDetail / sectionDetail / calendar).
 *
 * This is the SINGLE source of truth — the backend validators, the mobile app,
 * and the web dashboard all import from here. It is stored separately in the
 * database (`SystemConfiguration` category `SCREEN_FEATURES`, key
 * `SCREEN_CONFIGS`) and merged into app config at runtime.
 *
 * Note: super-admins always have implicit access to all screen features.
 *
 * Reconciled to the backend shape: the top-level set is
 * home / groups / directory / personDetail / sectionDetail / calendar. Mobile's
 * dead top-level `welcomeCard` / `profile` screens are intentionally dropped —
 * the welcome card lives under `home.features['welcome-card']` — and no stored
 * config uses them.
 */

import { z } from 'zod'
import { UserRoleSchema } from './navigation'

// ============================================================================
// Base Types
// ============================================================================

/**
 * Access control schema for screen features
 *
 * Two modes:
 * 1. Universal access: "*" - Everyone can access
 * 2. Role-based access: Object mapping roles to scope arrays
 *
 * Valid scopes per context:
 * - Person Detail: "*", "self", "my-students", "my-children", "parents-in-my-sections"
 * - Section Detail: "*", "sections-i-teach", "my-children-sections", "sections-im-enrolled-in"
 * - Home Widgets: "*"
 */
export const AccessControlSchema = z.union([
  z.literal('*'), // Universal access
  z.object({
    admin: z.array(z.string()).optional(),
    faculty: z.array(z.string()).optional(),
    staff: z.array(z.string()).optional(),
    parent: z.array(z.string()).optional(),
    student: z.array(z.string()).optional()
  })
])

// ============================================================================
// Screen Feature Configuration
// ============================================================================

/**
 * Base configuration for any screen feature
 *
 * All screen features must have:
 * - enabled: Whether the feature is active
 * - access: Who can access the feature (universal "*" or role-scope mapping)
 * - betaOnly: Optional flag to restrict to beta users
 * - order: Optional display order (personDetail / sectionDetail)
 */
export const ScreenFeatureConfigSchema = z.object({
  /** Whether this feature is enabled */
  enabled: z.boolean(),

  /**
   * Access control for this feature
   * - "*" for universal access (everyone can see)
   * - Object mapping roles to scopes for fine-grained control
   */
  access: AccessControlSchema,

  /**
   * Restrict feature to beta users only (for gradual rollout)
   * When true, only users marked as beta testers can access this feature
   */
  betaOnly: z.boolean().optional(),

  /**
   * Display order for this feature (lower numbers appear first)
   * Used by personDetail and sectionDetail screens to control feature ordering
   */
  order: z.number().optional()
})

// ============================================================================
// PersonDetail Screen Configuration
// ============================================================================

/**
 * Configuration for the relationships feature
 * Uses base ScreenFeatureConfig without additional properties
 */
const PersonDetailRelationshipsFeatureSchema = ScreenFeatureConfigSchema

/**
 * All possible features for the Person Detail screen
 */
const PersonDetailFeaturesSchema = z
  .object({
    /** Contact information tab */
    contact: ScreenFeatureConfigSchema.optional(),

    /** Classes/Sections the person is enrolled in or teaches */
    classes: ScreenFeatureConfigSchema.optional(),

    /** Student schedule view */
    schedule: ScreenFeatureConfigSchema.optional(),

    /** Assignments for students */
    assignments: ScreenFeatureConfigSchema.optional(),

    /** Groups the person belongs to */
    groups: ScreenFeatureConfigSchema.optional(),

    /** Family relationships (parents, children, guardians) */
    relationships: PersonDetailRelationshipsFeatureSchema.optional(),

    /**
     * SCH-1584 — show the Blackbaud business/work phone on the contact tab for
     * faculty and staff.
     *
     * Default OFF, and deliberately so: this is the one directory-displayed
     * field sourced from the extended user record rather than the directory
     * payload, and Blackbaud exposes no per-phone publish flag we can honour
     * (`PhoneRead` has none; `links[].shared` governs edit propagation between
     * household members, not visibility). Absent this key the gate resolves
     * false, so a school only shows business phones once its own admin asks —
     * do not add it to the system defaults, and do not enable it fleet-wide.
     *
     * The faculty/staff restriction is NOT this toggle's job: it is enforced in
     * the directory sync and again on the read path, so a parent never gets a
     * business phone no matter how this is configured.
     *
     * ONLY `enabled` is honoured. `access` is required by the shared
     * ScreenFeatureConfig shape but is INERT for this feature, and narrowing it
     * will not restrict anyone — do not use it as a privacy control. The reason
     * is structural: the directory listing transform renders one blob for the
     * whole school and is never handed a viewer, so it cannot evaluate a
     * role/relationship policy. Rather than let the server ship the field to
     * everyone while the client quietly hid it (config-shaped protection that
     * isn't enforced), both sides gate on `enabled` alone. Restricting WHO sees
     * business phones within a school would need the transform to become
     * viewer-aware first.
     */
    businessPhone: ScreenFeatureConfigSchema.optional()

    // No `classGrades` key. It was the SCH-1687 class-grades switch; since
    // SCH-1783 R17 nothing reads it, and the switch is the school's
    // `globalFeatures.classGrades` block. Some stored rows still carry it: this
    // object strips unknown keys, so those rows parse and the key is dropped.
  })
  .optional()

/**
 * Complete Person Detail screen configuration
 */
export const PersonDetailScreenConfigSchema = z.object({
  features: PersonDetailFeaturesSchema
})

// ============================================================================
// SectionDetail Screen Configuration
// ============================================================================

/**
 * Configuration for section assignments feature
 * Uses base ScreenFeatureConfig without additional properties
 */
const SectionDetailAssignmentsFeatureSchema = ScreenFeatureConfigSchema

/**
 * All possible features for the Section Detail screen
 */
const SectionDetailFeaturesSchema = z
  .object({
    /** Student roster for the section */
    students: ScreenFeatureConfigSchema.optional(),

    /** Parent roster for the section */
    parents: ScreenFeatureConfigSchema.optional(),

    /** Assignments posted to this section */
    assignments: SectionDetailAssignmentsFeatureSchema.optional(),

    /** News and announcements for the section */
    news: ScreenFeatureConfigSchema.optional()
  })
  .optional()

/**
 * Complete Section Detail screen configuration
 */
export const SectionDetailScreenConfigSchema = z.object({
  features: SectionDetailFeaturesSchema
})

// ============================================================================
// Home Screen Configuration
// ============================================================================

/**
 * Quick action button in the home screen
 */
const HomeScreenActionSchema = z.object({
  id: z.string(),
  label: z.string(),
  icon: z.string(),
  route: z.string().optional(),
  roles: z.array(UserRoleSchema).optional()
})

/**
 * Header UI element configuration
 */
const HeaderUIElementSchema = z.object({
  type: z.string(),
  visible: z.boolean()
})

/**
 * Welcome card action button
 *
 * `action` stays a loose `z.string()` on purpose. The backend read path parses
 * each screen entry independently and DROPS one that fails its schema, so a
 * stale or unrecognised action under a `z.enum` would take the whole `home`
 * entry down with it — losing the welcome card, the widgets and the header in
 * one go. The mobile handler already ignores an action it does not recognise,
 * so an unknown value degrades to "the button does nothing", never to "the home
 * screen lost its config". Same reasoning as `GroupsScreenConfigSchema.typeOrder`.
 */
const WelcomeCardActionButtonSchema = z.object({
  text: z.string(),
  action: z.string(),
  target: z.string().optional(),
  /**
   * Destination for the `external-link` / `web-view` actions. Absent from this
   * schema until SCH-1103, which meant every URL an admin typed was silently
   * stripped on write AND on read — the six buttons stored fleet-wide all
   * ended up target-less, so tapping them did nothing.
   */
  url: z.string().optional(),
  icon: z.string().optional(),
  accessibilityLabel: z.string().optional()
})

/**
 * Welcome card configuration (shown on home screen)
 * Extends ScreenFeatureConfigSchema with welcome-card-specific properties
 */
const WelcomeCardFeatureSchema = ScreenFeatureConfigSchema.extend({
  title: z.string().optional(),
  showLogo: z.boolean().optional(),
  htmlContent: z.string().optional(),
  actionButton: WelcomeCardActionButtonSchema.optional(),
  dismissible: z.boolean().optional(),
  showOnlyOnce: z.boolean().optional(),
  /** ISO 8601 timestamp of when the welcome card was last updated */
  updatedAt: z.iso.datetime().nullable().optional()
})

/**
 * Students-for-parents widget configuration
 * Shows parent's students with optional schedule display
 */
const StudentsForParentsWidgetFeatureSchema = ScreenFeatureConfigSchema.extend({
  /** Whether to show student schedules in the widget */
  showSchedule: z.boolean().optional()
})

/**
 * Faculty next class widget configuration
 * Shows faculty's upcoming class
 */
const FacultyNextClassWidgetFeatureSchema = ScreenFeatureConfigSchema.extend({
  // Add widget-specific properties here in the future
  // e.g., showClassLocation: z.boolean().optional()
})

/**
 * Home screen messages feature configuration
 * Controls the announcements/messages feed displayed on the home screen
 * These are notification feed items that can include images and rich content
 *
 * Uses the simpler visibleForRoles pattern (like global features) since
 * home screen messages don't need scoped access control.
 */
const HomeScreenMessagesFeatureSchema = z.object({
  /** Whether this feature is enabled */
  enabled: z.boolean(),

  /** Roles that can see this feature */
  visibleForRoles: z.array(UserRoleSchema),

  /** Restrict feature to beta users only (for gradual rollout) */
  betaOnly: z.boolean().optional(),

  /** How often to refresh the messages feed (in seconds) */
  refreshIntervalSeconds: z.number().optional(),

  /** Maximum number of messages to display on the home screen */
  maxMessagesToShow: z.number().optional()
})

/**
 * Home screen widget/feature configuration
 */
const HomeScreenFeaturesSchema = z
  .object({
    /** Welcome card shown on home screen */
    'welcome-card': WelcomeCardFeatureSchema.optional(),

    /** Widget showing parent's students */
    'students-for-parents-widget': StudentsForParentsWidgetFeatureSchema.optional(),

    /** Widget showing faculty's next class */
    'faculty-next-class-widget': FacultyNextClassWidgetFeatureSchema.optional(),

    /** Home screen messages/announcements feed */
    'home-screen-messages': HomeScreenMessagesFeatureSchema.optional()
  })
  .optional()

/**
 * Complete Home screen configuration
 */
export const HomeScreenConfigSchema = z.object({
  /** Layout style for home screen — dormant, unread; use `homeCardLayout` instead */
  layout: z.enum(['cards', 'list']).optional(),

  /** School-wide Home card layout. Absent/undefined ⇒ stack (today's vertical list). */
  homeCardLayout: z.enum(['stack', 'carousel']).optional(),

  /**
   * Paging dots under the Home card carousel. Absent/false ⇒ hidden. Only the
   * carousel layout reads it; 1.9+ apps only (1.8 never had the dots).
   */
  showCarouselDots: z.boolean().optional(),

  /** How often to refresh home screen data (seconds) */
  refreshIntervalSeconds: z.number().optional(),

  /** Whether pull-to-refresh is enabled */
  enablePullToRefresh: z.boolean().optional(),

  /** Header UI customization */
  header: z
    .object({
      leftSide: z
        .object({
          elements: z.array(HeaderUIElementSchema)
        })
        .optional(),
      rightSide: z
        .object({
          elements: z.array(HeaderUIElementSchema)
        })
        .optional(),
      elements: z.record(z.string(), z.any()).optional()
    })
    .optional(),

  /**
   * Neutral wash strength over the home background image (0–1). 0 = image at
   * full strength (no wash); higher = more dim. Black in dark mode, white in
   * light mode, decoupled from the theme palette. Absent/undefined ⇒ today's
   * faded-image look (no wash), so unset schools see no change (R8).
   */
  backgroundOverlayStrength: z
    .number()
    .min(0)
    .max(1)
    .optional()
    .describe('Neutral wash strength over the background image; 0 = image at full strength, higher = more dim'),

  /** Home screen widgets and features */
  features: HomeScreenFeaturesSchema
})

// ============================================================================
// Directory Screen Configuration
// ============================================================================

/**
 * Complete Directory screen configuration
 * Extends base ScreenFeatureConfig with directory-specific settings
 */
export const DirectoryScreenConfigSchema = ScreenFeatureConfigSchema.extend({
  /** Default sort order */
  defaultSort: z.enum(['name', 'role', 'grade', 'lastName']).optional(),

  /** Available filters */
  filters: z
    .object({
      showRoleFilter: z.boolean().optional(),
      showGradeFilter: z.boolean().optional(),
      showDirectoryFilter: z.boolean().optional()
    })
    .optional()
})

// ============================================================================
// Groups Screen Configuration
// ============================================================================

/**
 * Complete Groups screen configuration
 * Extends base ScreenFeatureConfig with groups-specific settings
 */
export const GroupsScreenConfigSchema = ScreenFeatureConfigSchema.extend({
  /** Post composer settings */
  postComposer: z
    .object({
      allowPhotos: z.boolean().optional(),
      allowVideos: z.boolean().optional()
    })
    .optional(),

  /**
   * Per-school order of the type sections on the Spaces list's "All" grouping.
   * Absent — the normal case — means the app's built-in order: Classes, Teams,
   * Activities, Community. Valid tokens are `classes`, `teams`, `activities`,
   * `community`.
   *
   * Deliberately a loose `string[]` rather than a `z.enum` (KTD2). The backend
   * read path parses each screen entry independently and DROPS one that fails
   * its schema, so a single stale or typo'd token under a strict enum would take
   * the whole `groups` entry down with it — silently losing `postComposer` too.
   * Validation therefore happens client-side instead: the mobile resolver
   * ignores unknown tokens, counts a duplicate once, and appends any omitted
   * category in default order. A malformed value degrades to "sections in an odd
   * order", never to "a section of spaces vanished".
   */
  typeOrder: z
    .array(z.string())
    .optional()
    .describe(
      'Order of the Spaces "All" type sections. Valid tokens: classes, teams, activities, community. ' +
        'Omitted categories append in default order; unknown tokens are ignored client-side.'
    )
})

// ============================================================================
// Calendar Screen Configuration
// ============================================================================

/**
 * Calendar source integration for a school.
 * - `blackbaud` (default): live-fetch from the Blackbaud Sky calendar (per-user token).
 * - `ical`: fetch the school's public iCal/ICS feed (token-free). The feed URL
 *   itself is stored separately in SystemConfiguration (category `integrations`,
 *   key `calendar_ical_feed_url`) — see the iCal calendar source feature.
 */
export const CalendarIntegrationSchema = z.enum(['blackbaud', 'ical']).prefault('blackbaud')

/**
 * Calendar screen configuration. The `config.integration` field selects which
 * calendar source backs the mobile `/calendar` endpoint for this school (the
 * integration is server-resolved), and `config.showCategoryBadges` controls the
 * category badge on mobile event cards. `config` is optional.
 */
export const CalendarScreenConfigSchema = ScreenFeatureConfigSchema.extend({
  config: z
    .object({
      /** Which calendar source backs this school's events (default `blackbaud`). */
      integration: CalendarIntegrationSchema,
      /**
       * Whether mobile calendar event cards show the event's category badge.
       * Absent or `true` shows it; `false` hides it. The calendar's category
       * filter menu is unaffected (SCH-3184).
       */
      showCategoryBadges: z.boolean().optional()
    })
    .optional()
})

// ============================================================================
// Main Screens Configuration Schema
// ============================================================================

/**
 * Complete screens configuration for the entire app
 *
 * This is the root schema for the `screenFeatures` portion of app config.
 * Each screen is optional - only include screens you want to configure.
 *
 * The root object keeps Zod's default object behavior: an unknown/custom screen
 * key does not fail validation, but is stripped from the parsed output (not
 * passed through). Tightening applies to the known per-screen shapes only.
 */
export const ScreensConfigSchema = z.object({
  /** Person detail modal configuration */
  personDetail: PersonDetailScreenConfigSchema.optional(),

  /** Section/Class detail screen configuration */
  sectionDetail: SectionDetailScreenConfigSchema.optional(),

  /** Home screen configuration */
  home: HomeScreenConfigSchema.optional(),

  /** Directory/People screen configuration */
  directory: DirectoryScreenConfigSchema.optional(),

  /** Groups screen configuration */
  groups: GroupsScreenConfigSchema.optional(),

  /** Calendar screen configuration (selects the calendar source integration) */
  calendar: CalendarScreenConfigSchema.optional()
})

// ============================================================================
// TypeScript Types
// ============================================================================

/**
 * Infer TypeScript types from the Zod schemas
 * Use these types in your TypeScript code
 */
export type AccessControl = z.infer<typeof AccessControlSchema>
export type ScreenFeatureConfig = z.infer<typeof ScreenFeatureConfigSchema>
export type PersonDetailScreenConfig = z.infer<typeof PersonDetailScreenConfigSchema>
export type SectionDetailScreenConfig = z.infer<typeof SectionDetailScreenConfigSchema>
export type HomeScreenConfig = z.infer<typeof HomeScreenConfigSchema>
export type WelcomeCardFeature = z.infer<typeof WelcomeCardFeatureSchema>
export type WelcomeCardActionButton = z.infer<typeof WelcomeCardActionButtonSchema>
export type StudentsForParentsWidgetFeature = z.infer<typeof StudentsForParentsWidgetFeatureSchema>
export type FacultyNextClassWidgetFeature = z.infer<typeof FacultyNextClassWidgetFeatureSchema>
export type HomeScreenMessagesFeature = z.infer<typeof HomeScreenMessagesFeatureSchema>
export type HomeScreenAction = z.infer<typeof HomeScreenActionSchema>
export type DirectoryScreenConfig = z.infer<typeof DirectoryScreenConfigSchema>
export type GroupsScreenConfig = z.infer<typeof GroupsScreenConfigSchema>
export type CalendarScreenConfig = z.infer<typeof CalendarScreenConfigSchema>
export type CalendarIntegration = z.infer<typeof CalendarIntegrationSchema>
export type ScreensConfig = z.infer<typeof ScreensConfigSchema>

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Validate screens configuration and return typed result
 *
 * @param data - Raw screens configuration data
 * @returns Validated and typed screens configuration
 * @throws ZodError if validation fails
 */
export function validateScreensConfig(data: unknown): ScreensConfig {
  return ScreensConfigSchema.parse(data)
}

/**
 * Safely parse screens configuration without throwing
 *
 * @param data - Raw screens configuration data
 * @returns Success or error result
 */
export function safeParseScreensConfig(data: unknown) {
  return ScreensConfigSchema.safeParse(data)
}
